import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Settings,
  Save,
  RotateCcw,
  Wifi,
  Clock,
  Timer,
  Server,
  AlertCircle,
  CheckCircle2,
  Info,
  RefreshCw,
  Zap,
  Users,
  MonitorSmartphone,
  Key,
  Eye,
  EyeOff,
  FlaskConical,
  Play,
  Download,
  ArrowUpCircle,
  Loader2
} from 'lucide-react';
import { Switch } from "@/components/ui/switch";
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import usePageTitle from '../hooks/usePageTitle';
import { cn } from '@/lib/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/*
 * Aurora (2026-08-23): every section is a glass card, colours are the semantic tokens, and the
 * long form stays readable on a phone (label above its control, the save bar sticks above the
 * bottom tab bar). The class strings below are shared so the sections read as one page.
 */
const CARD_HEAD = 'p-5 sm:p-6';
const CARD_BODY = 'p-5 pt-0 sm:p-6 sm:pt-0';
const SECTION_TITLE = 'font-heading text-base font-semibold flex flex-wrap items-center gap-2 text-foreground';
const FIELD_LABEL = 'text-sm font-medium text-foreground flex items-center gap-2';
const INPUT = 'bg-secondary/60 border-border placeholder:text-muted-foreground/70';
const NUM_INPUT = 'w-24 bg-secondary/60 border-border tnum';
const PANEL = 'rounded-xl border border-border bg-secondary/50';
/* Alert tones: the tint, the icon colour, body copy stays near-foreground for contrast. */
const ALERT = {
  warning: 'border-warning/30 bg-warning/10 text-foreground/90 [&>svg]:text-warning',
  success: 'border-success/30 bg-success/10 text-foreground/90 [&>svg]:text-success',
  accent: 'border-accent/30 bg-accent/10 text-foreground/90 [&>svg]:text-accent',
  info: 'border-info/30 bg-info/10 text-foreground/90 [&>svg]:text-info',
  muted: 'border-border bg-secondary/50 text-foreground/90 [&>svg]:text-muted-foreground',
};
const CHIP = {
  warning: 'rounded-full border-warning/30 bg-warning/10 text-warning',
  success: 'rounded-full border-success/30 bg-success/10 text-success',
  accent: 'rounded-full border-accent/30 bg-accent/10 text-accent',
  muted: 'rounded-full border-border bg-secondary/60 text-muted-foreground',
};

// ============ Server Update Panel Component ============
const ServerUpdatePanel = () => {
  const [checking, setChecking] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateInfo, setUpdateInfo] = useState(null);
  const [currentVersion, setCurrentVersion] = useState('...');

  // Fetch current version on mount
  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const resp = await fetch(`${API_BASE}/api/system-settings/server-update/version`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          setCurrentVersion(data.version || '0.0.0');
        }
      } catch (e) {
        console.error('Failed to fetch server version:', e);
      }
    };
    fetchVersion();
  }, []);

  const handleCheckUpdate = async () => {
    setChecking(true);
    setUpdateInfo(null);
    try {
      const token = localStorage.getItem('accessToken');
      const resp = await fetch(`${API_BASE}/api/system-settings/server-update/check`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json();
      setUpdateInfo(data);
      setCurrentVersion(data.current_version || currentVersion);

      if (data.update_available) {
        toast.info(`Update available: v${data.latest_version}`);
      } else if (data.error) {
        toast.error(`Check failed: ${data.error}`);
      } else {
        toast.success('Server is up to date!');
      }
    } catch (e) {
      toast.error('Failed to check for updates');
    } finally {
      setChecking(false);
    }
  };

  const handleApplyUpdate = async () => {
    if (!updateInfo?.update_available) return;

    setUpdating(true);
    try {
      const token = localStorage.getItem('accessToken');
      const resp = await fetch(`${API_BASE}/api/system-settings/server-update/apply`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json();

      if (data.success) {
        toast.success(data.message, { duration: 10000 });
        setCurrentVersion(data.version);
        setUpdateInfo(null);
        // The server will restart in ~3 seconds
        setTimeout(() => {
          toast.info('Server is restarting... Page will reload in 10 seconds.', { duration: 10000 });
        }, 3000);
        setTimeout(() => {
          window.location.reload();
        }, 13000);
      } else {
        toast.error(data.message || 'Update failed');
      }
    } catch (e) {
      toast.error('Update request failed');
    } finally {
      setUpdating(false);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <Card className="aurora-card" data-testid="server-update-card">
      <CardHeader className={cn(CARD_HEAD, 'pb-3')}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-2">
            <ArrowUpCircle className="h-5 w-5 shrink-0 text-primary" />
            <div className="min-w-0">
              <CardTitle className={SECTION_TITLE}>Server Updates</CardTitle>
              <CardDescription className="text-xs">
                Current version: <span className="font-mono text-primary tnum">{currentVersion}</span>
              </CardDescription>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCheckUpdate}
            disabled={checking || updating}
            className="tap self-start sm:self-auto sm:shrink-0"
            data-testid="check-server-update-btn"
          >
            {checking ? (
              <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> Checking...</>
            ) : (
              <><RefreshCw className="h-3 w-3 mr-1.5" /> Check for Updates</>
            )}
          </Button>
        </div>
      </CardHeader>

      {updateInfo && (
        <CardContent className={CARD_BODY}>
          {updateInfo.update_available ? (
            <div className="space-y-3">
              <div className="rounded-xl border border-primary/30 bg-primary/10 p-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium text-primary">
                    v{updateInfo.latest_version} available
                  </span>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground tnum">
                    {updateInfo.version_date && <span>{updateInfo.version_date}</span>}
                    {updateInfo.file_size > 0 && <span>{formatSize(updateInfo.file_size)}</span>}
                  </div>
                </div>
                {updateInfo.release_notes && (
                  <p className="text-xs text-foreground/90">{updateInfo.release_notes}</p>
                )}
              </div>
              <Button
                onClick={handleApplyUpdate}
                disabled={updating}
                className="tap w-full"
                data-testid="apply-server-update-btn"
              >
                {updating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Updating & Restarting...</>
                ) : (
                  <><Download className="h-4 w-4 mr-2" /> Update to v{updateInfo.latest_version}</>
                )}
              </Button>
            </div>
          ) : updateInfo.error ? (
            <div className="rounded-xl border border-danger/30 bg-danger/10 p-3">
              <p className="text-xs text-danger">{updateInfo.error}</p>
            </div>
          ) : (
            <div className="rounded-xl border border-success/30 bg-success/10 p-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span className="text-sm text-success">Server is up to date</span>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};


// Scheduling mode options
const SCHEDULING_MODES = {
  legacy_only: {
    label: 'Legacy Polling + Client-Side Locking',
    description: 'Java-style: All clients poll server, use file-based device lock queue',
    icon: RefreshCw,
    color: 'text-muted-foreground'
  },
  user_choice: {
    label: 'User Choice (Recommended for Migration)',
    description: 'Users can choose in their client GUI which mode to use. Best for phased rollout.',
    icon: Users,
    color: 'text-warning',
    recommended: true
  },
  websocket_only: {
    label: 'Server WebSocket + Server-Side Scheduling',
    description: 'Modern: Server pushes RUN_SESSION commands, manages all device locks centrally',
    icon: Zap,
    color: 'text-success'
  }
};

/* One masked API-key field: the input, the show/hide eye, the "Configured" chip. */
function SecretField({ id, label, icon: Icon, iconTone, hint, placeholder, value, onChange, shown, onToggle, testId }) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-4 md:items-start">
      <div className="space-y-1.5">
        <Label htmlFor={id} className={FIELD_LABEL}>
          <Icon className={cn('h-4 w-4', iconTone)} />
          {label}
        </Label>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2 md:col-span-2">
        <div className="relative min-w-0 flex-1">
          <Input
            id={id}
            type={shown ? "text" : "password"}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            className={cn(INPUT, 'pr-11')}
            data-testid={testId}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="tap absolute right-0 top-1/2 h-9 w-10 -translate-y-1/2 px-0 text-muted-foreground hover:text-foreground"
            onClick={onToggle}
            aria-label={shown ? `Hide ${label}` : `Show ${label}`}
            title={shown ? 'Hide key' : 'Show key'}
          >
            {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>
        {value && (
          <Badge variant="outline" className={CHIP.success}>Configured</Badge>
        )}
      </div>
    </div>
  );
}

/* One numeric setting: label + hint on the left, a small number input with its unit on the right. */
function NumberSetting({ id, label, icon: Icon, hint, unit, min, max, value, onChange, testId }) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 md:items-start">
      <div className="space-y-1.5">
        <Label htmlFor={id} className={FIELD_LABEL}>
          <Icon className="h-4 w-4 text-muted-foreground" />
          {label}
        </Label>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          type="number"
          min={min}
          max={max}
          value={value}
          onChange={onChange}
          className={NUM_INPUT}
          data-testid={testId}
        />
        <span className="text-sm text-muted-foreground">{unit}</span>
      </div>
    </div>
  );
}

function SettingsSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true" aria-label="Loading system settings" data-testid="system-settings-skeleton">
      <div className="aurora-card p-5 sm:p-6">
        <div className="h-4 w-48 animate-pulse rounded-xl bg-secondary/60" />
        <div className="mt-2 h-3 w-72 max-w-full animate-pulse rounded-xl bg-secondary/60" />
        <div className="mt-5 space-y-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-20 w-full animate-pulse rounded-xl bg-secondary/60" />)}
        </div>
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} className="aurora-card p-5 sm:p-6">
          <div className="h-4 w-40 animate-pulse rounded-xl bg-secondary/60" />
          <div className="mt-2 h-3 w-64 max-w-full animate-pulse rounded-xl bg-secondary/60" />
          <div className="mt-5 h-16 w-full animate-pulse rounded-xl bg-secondary/60" />
        </div>
      ))}
    </div>
  );
}

export default function SystemSettingsPage() {
  usePageTitle('System settings');
  const { user, accessToken } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [apiKeys, setApiKeys] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [connectedDevices, setConnectedDevices] = useState(0);
  const [showRocketApiKey, setShowRocketApiKey] = useState(false);
  const [showLamadavaKey, setShowLamadavaKey] = useState(false);
  const [showOpenAIKey, setShowOpenAIKey] = useState(false);
  const [showGeminiKey, setShowGeminiKey] = useState(false);

  // Check if user is owner
  const isOwner = user?.role === 'owner';

  /*
   * r16o3 v3-backlog (2026-10-24, future todo 12): the build's react-hooks/exhaustive-deps warning here ("missing dependencies:
   * fetchApiKeys, fetchConnectedDevices, fetchSettings") failed every CI=true build. The three loaders are re-created on every
   * render, so listing them would re-run this effect (three fetches, a new 30 s timer) on EVERY render. Instead the effect reads
   * the loaders of the render it runs after through a ref - the very functions it used to close over - so it still runs exactly
   * when [isOwner, navigate] change, fetches each URL once, and the timer polls the same function as before.
   */
  const loadersRef = useRef(null);

  useEffect(() => {
    if (!isOwner) {
      toast.error('Access Denied', {
        description: 'Only the owner can access system settings'
      });
      navigate('/dashboard');
      return;
    }

    const loaders = loadersRef.current;
    loaders.fetchSettings();
    loaders.fetchApiKeys();
    loaders.fetchConnectedDevices();

    // Poll connected devices every 30 seconds
    const interval = setInterval(loaders.fetchConnectedDevices, 30000);
    return () => clearInterval(interval);
  }, [isOwner, navigate]);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/system-settings`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch settings');

      const data = await response.json();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchApiKeys = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/system-settings/api-keys`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setApiKeys(data);
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
    }
  };

  const fetchConnectedDevices = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/ws/devices/connected`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setConnectedDevices(data.connected_devices || 0);
      }
    } catch (error) {
      console.error('Error fetching connected devices:', error);
    }
  };

  // the loaders of THIS render, for the mount effect above. Assigned in a layout effect, not during render (review should-fix,
  // 2026-10-24: React discourages writing a ref while rendering, and newer react-hooks lint rules flag it): every layout effect
  // of a commit runs before any useEffect of that commit, so the mount effect still reads this render's loaders.
  useLayoutEffect(() => {
    loadersRef.current = { fetchSettings, fetchApiKeys, fetchConnectedDevices };
  });

  const handleSettingChange = (category, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const handleApiKeyChange = (field, value) => {
    setApiKeys(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save system settings
      const response = await fetch(`${API_BASE}/api/system-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          device_scheduling: settings.device_scheduling,
          notifications: settings.notifications
        })
      });

      if (!response.ok) throw new Error('Failed to save settings');

      const data = await response.json();
      setSettings(data);

      // Save API keys if they were modified
      if (apiKeys) {
        const apiKeysResponse = await fetch(`${API_BASE}/api/system-settings/api-keys`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(apiKeys)
        });

        if (!apiKeysResponse.ok) {
          console.error('Failed to save API keys');
        }
      }

      setHasChanges(false);

      toast.success('Settings saved', {
        description: 'System settings have been updated successfully'
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset all settings to defaults?')) {
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/system-settings/reset-to-defaults`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) throw new Error('Failed to reset settings');

      const data = await response.json();
      setSettings(data);
      setHasChanges(false);

      toast.success('Settings reset', {
        description: 'All settings have been reset to defaults'
      });
    } catch (error) {
      console.error('Error resetting settings:', error);
      toast.error('Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  // The header is the same in every state so the page does not jump between them.
  const header = (
    <PageHeader
      icon={Settings}
      title="System Settings"
      description="Global configuration for device scheduling and notifications"
      actions={(
        <>
          {/* Connected Devices Badge */}
          <Badge
            variant="outline"
            className={cn('flex items-center gap-1.5 px-3 py-1 tnum', connectedDevices > 0 ? CHIP.success : CHIP.muted)}
            data-testid="connected-devices-badge"
          >
            <Wifi className="h-3 w-3" />
            {connectedDevices} device{connectedDevices !== 1 ? 's' : ''} connected
          </Badge>

          {hasChanges && (
            <Badge variant="outline" className={cn('px-3 py-1 animate-fade-in', CHIP.warning)} data-testid="unsaved-changes-badge">
              Unsaved Changes
            </Badge>
          )}
        </>
      )}
    />
  );

  if (loading) {
    return (
      <div data-testid="system-settings-page">
        {header}
        <SettingsSkeleton />
      </div>
    );
  }

  if (!settings) {
    return (
      <div data-testid="system-settings-page">
        {header}
        <div className="aurora-card p-8 text-center sm:p-10" role="alert" data-testid="system-settings-error">
          <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-danger/10 text-danger ring-1 ring-danger/20">
            <AlertCircle className="h-6 w-6" />
          </span>
          <h3 className="mt-4">Could not load system settings</h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">
            Failed to load system settings. Please try refreshing the page.
          </p>
          <Button
            variant="outline"
            className="tap mt-5 rounded-full"
            onClick={() => { setLoading(true); fetchSettings(); }}
            data-testid="system-settings-retry"
          >
            <RefreshCw className="h-4 w-4" /> Try again
          </Button>
        </div>
      </div>
    );
  }

  const { device_scheduling, notifications } = settings;
  const currentMode = device_scheduling.scheduling_mode || 'legacy_only';

  return (
    <div className="space-y-6" data-testid="system-settings-page">
      {/* Header */}
      {header}

      {/* Scheduling Mode Selection - PRIMARY SETTING */}
      <Card className="aurora-card aurora-hero">
        <CardHeader className={CARD_HEAD}>
          <CardTitle className={SECTION_TITLE}>
            <MonitorSmartphone className="h-5 w-5 text-accent" />
            Slot Scheduling Mode
          </CardTitle>
          <CardDescription>
            Choose how clients communicate with the server for session scheduling
          </CardDescription>
        </CardHeader>
        <CardContent className={cn(CARD_BODY, 'space-y-4')}>
          <RadioGroup
            value={currentMode}
            onValueChange={(value) => handleSettingChange('device_scheduling', 'scheduling_mode', value)}
            className="space-y-3"
            data-testid="scheduling-mode-radio"
          >
            {Object.entries(SCHEDULING_MODES).map(([value, mode]) => {
              const Icon = mode.icon;
              const isSelected = currentMode === value;

              return (
                <div
                  key={value}
                  className={cn(
                    'flex items-start gap-3 rounded-xl border p-4 transition-colors cursor-pointer',
                    isSelected
                      ? 'border-accent bg-accent/10'
                      : 'border-border bg-secondary/40 hover:border-muted-foreground/40'
                  )}
                  onClick={() => handleSettingChange('device_scheduling', 'scheduling_mode', value)}
                >
                  <RadioGroupItem value={value} id={value} className="mt-1 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <Label
                      htmlFor={value}
                      className={cn(
                        'flex flex-wrap items-center gap-2 text-base font-medium cursor-pointer',
                        isSelected ? 'text-foreground' : 'text-foreground/90'
                      )}
                    >
                      <Icon className={cn('h-4 w-4 shrink-0', mode.color)} />
                      {mode.label}
                      {mode.recommended && (
                        <Badge variant="outline" className={cn('text-xs', CHIP.warning)}>
                          Recommended
                        </Badge>
                      )}
                    </Label>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {mode.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </RadioGroup>

          {/* Mode-specific info boxes */}
          {currentMode === 'user_choice' && (
            <Alert className={cn(ALERT.warning, 'mt-4')}>
              <Users className="h-4 w-4" />
              <AlertDescription>
                <strong className="text-warning">User Choice Mode:</strong> When enabled, each user&apos;s client GUI will show a toggle
                &quot;Use NEW WebSocket Scheduling&quot;. Users can opt-in to test the new system while others
                continue using legacy polling.
              </AlertDescription>
            </Alert>
          )}

          {currentMode === 'websocket_only' && (
            <Alert className={cn(ALERT.success, 'mt-4')}>
              <Zap className="h-4 w-4" />
              <AlertDescription>
                <strong className="text-success">WebSocket Only Mode:</strong> All clients will be required to use WebSocket connections.
                Clients using legacy polling will need to update. This is the most efficient mode but requires
                all clients to be updated.
              </AlertDescription>
            </Alert>
          )}

          {currentMode === 'legacy_only' && (
            <Alert className={cn(ALERT.muted, 'mt-4')}>
              <RefreshCw className="h-4 w-4" />
              <AlertDescription>
                <strong className="text-foreground">Legacy Mode:</strong> All clients use the traditional polling mechanism with
                client-side file-based device locking (similar to the Java application).
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Test Mode for Specific Accounts - For Testing */}
      <Card className="aurora-card">
        <CardHeader className={CARD_HEAD}>
          <CardTitle className={SECTION_TITLE}>
            <FlaskConical className="h-5 w-5 text-warning" />
            Test Mode (Placeholder Sessions)
            <Badge variant="outline" className={cn('text-xs', CHIP.warning)}>
              Development
            </Badge>
          </CardTitle>
          <CardDescription>
            Enable placeholder sessions for specific test accounts to verify timing logic without running real automation
          </CardDescription>
        </CardHeader>
        <CardContent className={cn(CARD_BODY, 'space-y-4')}>
          {/* Enable Test Mode Toggle */}
          <div className={cn(PANEL, 'flex items-center justify-between gap-4 p-4')}>
            <div className="min-w-0 space-y-1">
              <Label htmlFor="test-mode-toggle" className={FIELD_LABEL}>
                <FlaskConical className="h-4 w-4 shrink-0 text-warning" />
                Enable Test Mode for Specific Accounts
              </Label>
              <p className="text-sm text-muted-foreground">
                When enabled, accounts listed below will run quick placeholder sessions (~5 seconds) instead of real automation
              </p>
            </div>
            <Switch
              id="test-mode-toggle"
              className="shrink-0"
              checked={device_scheduling.test_mode_enabled || false}
              onCheckedChange={(checked) => handleSettingChange('device_scheduling', 'test_mode_enabled', checked)}
              data-testid="test-mode-toggle"
            />
          </div>

          {/* Test Account Emails - Only shown when test mode is enabled */}
          {device_scheduling.test_mode_enabled && (
            <div className="space-y-3 rounded-xl border border-warning/30 bg-warning/10 p-4">
              <Label htmlFor="test-mode-emails" className={FIELD_LABEL}>
                <Users className="h-4 w-4 text-warning" />
                Test Account Email IDs
              </Label>
              <p className="text-xs text-muted-foreground">
                Enter the email IDs of accounts that should run placeholder sessions (comma-separated)
              </p>
              <Input
                id="test-mode-emails"
                type="text"
                placeholder="test@example.com, test2@example.com, automation-test@domain.com"
                value={device_scheduling.test_mode_account_emails || ''}
                onChange={(e) => handleSettingChange('device_scheduling', 'test_mode_account_emails', e.target.value)}
                className={cn(INPUT, 'font-mono text-sm')}
                data-testid="test-mode-emails-input"
              />
              <p className="text-xs text-foreground/90">
                <strong className="text-warning">Note:</strong> These accounts will complete sessions in ~5 seconds for timing verification.
                All other accounts will run real automation.
              </p>
            </div>
          )}

          {device_scheduling.test_mode_enabled && (
            <Alert className={ALERT.warning}>
              <FlaskConical className="h-4 w-4" />
              <AlertDescription>
                <strong className="text-warning">Test Mode Active:</strong> Accounts matching the email list above will run placeholder
                sessions. Use this to test scheduling timing, work windows, and wait intervals without affecting
                real accounts.
              </AlertDescription>
            </Alert>
          )}

          {!device_scheduling.test_mode_enabled && (
            <Alert className={ALERT.success}>
              <Play className="h-4 w-4" />
              <AlertDescription>
                <strong className="text-success">Test Mode Disabled:</strong> All accounts will run the full automation workflow
                including posting, following, liking, and other configured actions.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Preview Server URL Override */}
      <Card className="aurora-card" data-testid="preview-url-override-card">
        <CardHeader className={CARD_HEAD}>
          <CardTitle className={SECTION_TITLE}>
            <Server className="h-5 w-5 text-accent" />
            Preview Server URL Override
          </CardTitle>
          <CardDescription>
            Route specific user accounts to a preview/staging server for testing new features
          </CardDescription>
        </CardHeader>
        <CardContent className={cn(CARD_BODY, 'space-y-4')}>
          {/* Enable Toggle */}
          <div className={cn(PANEL, 'flex items-center justify-between gap-4 p-3 sm:p-4')}>
            <div className="min-w-0">
              <Label htmlFor="preview-url-toggle" className={FIELD_LABEL}>
                <Server className="h-4 w-4 shrink-0 text-accent" />
                Enable Preview URL Override
              </Label>
              <p className="mt-1 text-xs text-muted-foreground">
                When enabled, accounts listed below will connect to the preview server instead of the main server
              </p>
            </div>
            <Switch
              id="preview-url-toggle"
              className="shrink-0"
              checked={device_scheduling.preview_url_override_enabled || false}
              onCheckedChange={(checked) => handleSettingChange('device_scheduling', 'preview_url_override_enabled', checked)}
              data-testid="preview-url-toggle"
            />
          </div>

          {device_scheduling.preview_url_override_enabled && (
            <div className="space-y-4 border-l-2 border-accent/30 pl-4">
              {/* Preview Server URL */}
              <div className="space-y-2">
                <label htmlFor="preview-server-url" className="text-sm font-medium text-foreground/90">Preview Server URL</label>
                <p className="text-xs text-muted-foreground">
                  The URL of the preview/staging server (e.g., Emergent preview deployment)
                </p>
                <Input
                  id="preview-server-url"
                  placeholder="https://session-logging.preview.emergentagent.com"
                  value={device_scheduling.preview_server_url || ''}
                  onChange={(e) => handleSettingChange('device_scheduling', 'preview_server_url', e.target.value)}
                  className={cn(INPUT, 'font-mono text-sm')}
                  data-testid="preview-server-url-input"
                />
              </div>

              {/* Account Emails */}
              <div className="space-y-2">
                <label htmlFor="preview-url-emails" className="text-sm font-medium text-foreground/90">Account Emails (comma-separated)</label>
                <p className="text-xs text-muted-foreground">
                  Client apps of these users will automatically switch to the preview server URL after login
                </p>
                <Input
                  id="preview-url-emails"
                  placeholder="test@example.com, beta-tester@domain.com"
                  value={device_scheduling.preview_url_account_emails || ''}
                  onChange={(e) => handleSettingChange('device_scheduling', 'preview_url_account_emails', e.target.value)}
                  className={cn(INPUT, 'font-mono text-sm')}
                  data-testid="preview-url-emails-input"
                />
              </div>

              <Alert className={ALERT.accent}>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong className="text-accent">How it works:</strong> After login, the client app checks if its account email matches this list.
                  If matched, all API calls are redirected to the preview server. The user doesn't need to edit their config file.
                </AlertDescription>
              </Alert>
            </div>
          )}
        </CardContent>
      </Card>

      {/* WebSocket Settings - Show when WebSocket is possible */}
      {(currentMode === 'websocket_only' || currentMode === 'user_choice') && (
        <Card className="aurora-card">
          <CardHeader className={CARD_HEAD}>
            <CardTitle className={SECTION_TITLE}>
              <Zap className="h-5 w-5 text-success" />
              WebSocket Scheduling Settings
            </CardTitle>
            <CardDescription>
              Settings for server-side WebSocket scheduling (when enabled)
            </CardDescription>
          </CardHeader>
          <CardContent className={cn(CARD_BODY, 'space-y-6')}>

            {/* WebSocket Heartbeat Interval */}
            <NumberSetting
              id="heartbeat-interval"
              icon={Clock}
              label="WebSocket Heartbeat Interval"
              hint="How often clients send heartbeat to keep connection alive"
              unit="seconds"
              min={10}
              max={300}
              value={device_scheduling.websocket_heartbeat_interval_secs}
              onChange={(e) => handleSettingChange('device_scheduling', 'websocket_heartbeat_interval_secs', parseInt(e.target.value) || 60)}
              testId="heartbeat-interval-input"
            />

            <Separator className="bg-border" />

            {/* Max Session Duration */}
            <NumberSetting
              id="max-session-duration"
              icon={Timer}
              label="Max Session Duration"
              hint="Force release device lock if session exceeds this duration"
              unit="minutes"
              min={30}
              max={480}
              value={device_scheduling.max_session_duration_mins}
              onChange={(e) => handleSettingChange('device_scheduling', 'max_session_duration_mins', parseInt(e.target.value) || 120)}
              testId="max-session-duration-input"
            />

            <Separator className="bg-border" />

            {/* WebSocket Reconnect Max Interval */}
            <NumberSetting
              id="reconnect-interval"
              icon={Wifi}
              label="WebSocket Reconnect Max Interval"
              hint="Maximum wait between reconnection attempts (exponential backoff)"
              unit="seconds"
              min={10}
              max={300}
              value={device_scheduling.websocket_reconnect_max_interval_secs}
              onChange={(e) => handleSettingChange('device_scheduling', 'websocket_reconnect_max_interval_secs', parseInt(e.target.value) || 60)}
              testId="reconnect-interval-input"
            />

            <Separator className="bg-border" />

            {/* Device Lock Queue Timeout */}
            <NumberSetting
              id="queue-timeout"
              icon={Clock}
              label="Device Lock Queue Timeout"
              hint="Remove slot from queue if not ready when its turn comes"
              unit="minutes"
              min={5}
              max={120}
              value={device_scheduling.device_lock_queue_timeout_mins}
              onChange={(e) => handleSettingChange('device_scheduling', 'device_lock_queue_timeout_mins', parseInt(e.target.value) || 30)}
              testId="queue-timeout-input"
            />

            <Separator className="bg-border" />

            {/* Scheduler Check Interval */}
            <NumberSetting
              id="scheduler-interval"
              icon={Server}
              label="Scheduler Check Interval"
              hint="How often server checks if any slot is ready to run"
              unit="seconds"
              min={1}
              max={60}
              value={device_scheduling.scheduler_check_interval_secs}
              onChange={(e) => handleSettingChange('device_scheduling', 'scheduler_check_interval_secs', parseInt(e.target.value) || 5)}
              testId="scheduler-interval-input"
            />
          </CardContent>
        </Card>
      )}

      {/* Legacy Polling Settings - Show when Legacy is possible */}
      {(currentMode === 'legacy_only' || currentMode === 'user_choice') && (
        <Card className="aurora-card">
          <CardHeader className={CARD_HEAD}>
            <CardTitle className={SECTION_TITLE}>
              <RefreshCw className="h-5 w-5 text-muted-foreground" />
              Legacy Polling Settings
            </CardTitle>
            <CardDescription>
              Settings for client-side polling and device locking (when enabled)
            </CardDescription>
          </CardHeader>
          <CardContent className={cn(CARD_BODY, 'space-y-6')}>

            {/* Polling Interval */}
            <NumberSetting
              id="polling-interval"
              icon={RefreshCw}
              label="Polling Interval"
              hint="How often client polls /worker/status endpoint"
              unit="seconds"
              min={10}
              max={300}
              value={device_scheduling.polling_interval_secs || 60}
              onChange={(e) => handleSettingChange('device_scheduling', 'polling_interval_secs', parseInt(e.target.value) || 60)}
              testId="polling-interval-input"
            />

            <Separator className="bg-border" />

            {/* Device Lock Timeout */}
            <NumberSetting
              id="device-lock-timeout"
              icon={Timer}
              label="Device Lock Timeout"
              hint="Max time to wait for device lock in file-based queue"
              unit="seconds"
              min={60}
              max={600}
              value={device_scheduling.device_lock_timeout_secs || 300}
              onChange={(e) => handleSettingChange('device_scheduling', 'device_lock_timeout_secs', parseInt(e.target.value) || 300)}
              testId="device-lock-timeout-input"
            />
          </CardContent>
        </Card>
      )}

      {/* API Keys Settings - For External Services */}
      <Card className="aurora-card">
        <CardHeader className={CARD_HEAD}>
          <CardTitle className={SECTION_TITLE}>
            <Key className="h-5 w-5 text-warning" />
            API Keys (Scraping Services)
          </CardTitle>
          <CardDescription>
            Configure API keys for external scraping services used by the client for post reposting
          </CardDescription>
        </CardHeader>
        <CardContent className={cn(CARD_BODY, 'space-y-6')}>

          {/* RocketAPI Key */}
          <SecretField
            id="rocket-api-key"
            icon={Key}
            iconTone="text-warning"
            label="RocketAPI Key"
            hint="API key from rocketapi.io for Instagram scraping"
            placeholder="Enter RocketAPI key..."
            value={apiKeys?.rocket_api_key || ''}
            onChange={(e) => handleApiKeyChange('rocket_api_key', e.target.value)}
            shown={showRocketApiKey}
            onToggle={() => setShowRocketApiKey(!showRocketApiKey)}
            testId="rocket-api-key-input"
          />

          <Separator className="bg-border" />

          {/* Lamadava Key */}
          <SecretField
            id="lamadava-api-key"
            icon={Key}
            iconTone="text-info"
            label="Lamadava Key (Backup)"
            hint="Backup API key from Lamadava for Instagram scraping"
            placeholder="Enter Lamadava key (optional)..."
            value={apiKeys?.lamadava_api_key || ''}
            onChange={(e) => handleApiKeyChange('lamadava_api_key', e.target.value)}
            shown={showLamadavaKey}
            onToggle={() => setShowLamadavaKey(!showLamadavaKey)}
            testId="lamadava-api-key-input"
          />

          <Separator className="bg-border" />

          {/* AI Detection API Keys Section */}
          <div className="space-y-2 pt-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-accent">AI Detection API Keys</span>
              <Badge variant="outline" className={cn('text-xs', CHIP.accent)}>Advanced</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              These keys are used for AI-powered element detection when automation encounters unknown UI elements.
              The system will automatically fallback through models (cheapest first) until detection succeeds.
            </p>
          </div>

          {/* OpenAI API Key */}
          <SecretField
            id="openai-api-key"
            icon={Key}
            iconTone="text-success"
            label="OpenAI API Key"
            hint="For GPT-4o Vision (highest accuracy, $5/1M tokens)"
            placeholder="sk-proj-..."
            value={apiKeys?.openai_api_key || ''}
            onChange={(e) => handleApiKeyChange('openai_api_key', e.target.value)}
            shown={showOpenAIKey}
            onToggle={() => setShowOpenAIKey(!showOpenAIKey)}
            testId="openai-api-key-input"
          />

          <Separator className="bg-border" />

          {/* Gemini API Key */}
          <SecretField
            id="gemini-api-key"
            icon={Key}
            iconTone="text-info"
            label="Google Gemini API Key"
            hint="For Gemini 2.0 Flash ($0.10/1M) & 2.5 Pro ($1.25/1M)"
            placeholder="AIzaSy..."
            value={apiKeys?.gemini_api_key || ''}
            onChange={(e) => handleApiKeyChange('gemini_api_key', e.target.value)}
            shown={showGeminiKey}
            onToggle={() => setShowGeminiKey(!showGeminiKey)}
            testId="gemini-api-key-input"
          />

          <Alert className={cn(ALERT.warning, 'mt-4')}>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong className="text-warning">API Keys:</strong> These keys are used by the Python client for direct Instagram scraping
              when post reposting is enabled. Get your RocketAPI key from{' '}
              <a href="https://rocketapi.io" target="_blank" rel="noopener noreferrer" className="text-warning underline">
                rocketapi.io
              </a>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Info Box */}
      <Alert className={ALERT.info}>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong className="text-info">Note:</strong> Changes to scheduling mode require clients to restart.
          Connected devices will receive updated settings automatically.
        </AlertDescription>
      </Alert>

      {/* ============ Server Update Section ============ */}
      <ServerUpdatePanel />

      {/* Action Buttons - a glass bar that sticks above the mobile tab bar (< lg) so Save is always
          within reach on the long form; on desktop it floats at the bottom of the viewport. */}
      <div
        className="aurora-card sticky z-20 bottom-[calc(var(--bottombar-h)+var(--safe-bottom)+0.5rem)] lg:bottom-4 flex flex-wrap items-center justify-end gap-2 p-3 sm:p-4"
        data-testid="settings-action-bar"
      >
        {hasChanges && (
          <span className="mr-auto text-xs text-muted-foreground">You have unsaved changes</span>
        )}
        <Button
          variant="outline"
          onClick={handleReset}
          disabled={saving}
          className="tap"
          data-testid="reset-defaults-btn"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset to Defaults
        </Button>

        <Button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="tap shadow-glow"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
